import type { AIChatBlock, AIChatCard, AIChatConversationSummary } from '@recipes/contracts';
import { detectDictationEnd } from '@recipes/domain';
import { Button, Textarea, cn } from '@recipes/ui';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  BookOpen,
  Check,
  ChefHat,
  ChevronLeft,
  ClipboardList,
  History,
  Mic,
  Plus,
  Send,
  Shrink,
  Sparkles,
  Square,
  Trash2,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { toast } from '@/components/feedback/toaster';
import { youtubeId } from '@/features/recipes/video-links';
import { AiConsentNotice } from '@/features/shared/ai-consent-notice';
import { useAiConsent, useConfig, useFeature, useProfile } from '@/features/shared/hooks';
import { useHoldOrTap } from '@/features/speech/use-hold-or-tap';
import { usePushToTalk } from '@/features/speech/use-push-to-talk';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';
import { currentLocale } from '@/lib/i18n';
import { toBcp47 } from '@/lib/speech/recognition';
import { ProcessingIndicator } from '@/features/shared/processing-indicator';
import { formatDate } from '@/features/shared/labels';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';
import { useAuth } from '@/lib/auth/auth-provider';
import { queryKeys } from '@/lib/query-keys';
import { SentenceBuffer, useSpeaker } from './chat-voice';
import { MarkdownLite, type RenderLink } from './markdown-lite';
import { useAiChatConversation, useAiChatStatus } from './use-ai-chat';

const SUGGESTIONS = ['pantry', 'cook', 'internet', 'video'] as const;

/** Where a card leads. */
function cardPath(card: AIChatCard): string {
  switch (card.kind) {
    case 'recipe':
      return `/recipes/${card.id}`;
    case 'generation':
      return `/add/ai/${card.id}`;
    case 'import':
      return `/add/import/${card.id}`;
    case 'shoppingList':
      return `/shopping/${card.id}`;
  }
}

/**
 * The AI chat (docs/ai-chat.md): a button at the bottom right opens a drawer that stays open
 * while the user works. Conversations, streamed answers, tool lines with cards and the
 * questions before a change live here; the model runs in the ai-chat container behind the API.
 */
export function AiChatDock() {
  const { t, i18n } = useTranslation();
  const { status: authStatus } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<'chat' | 'list'>('chat');
  const [input, setInput] = useState('');
  const [unread, setUnread] = useState(false);
  const aiConsent = useAiConsent();
  // "Rezept erstellen" behind a link (user 2026-09-30): the URL that is being turned into a draft.
  const [linkBusy, setLinkBusy] = useState<string | null>(null);
  const openRef = useRef(open);
  openRef.current = open;
  const listRef = useRef<HTMLDivElement>(null);
  const fullscreen = location.pathname.startsWith('/cook/');
  const signedIn = authStatus === 'authenticated' && !fullscreen;
  const statusQuery = useAiChatStatus(signedIn);

  // Talking (user 2026-10-01, as in Ubu): the microphone button (tap = on until the next tap,
  // hold = push-to-talk) and the voice that reads the answers – both from the app's speech
  // helpers and the profile's speech settings. With the microphone switched on, the chat
  // listens again after every answer, until the button is tapped again or nothing was said.
  const online = useOnlineStatus();
  const speechEnabled = useFeature('speech');
  const { data: config } = useConfig();
  const { data: profile } = useProfile();
  const locale = currentLocale();
  const readAloud = profile?.speechPreferences.readAloudEnabled ?? true;
  const speaker = useSpeaker({
    lang: toBcp47(locale),
    rate: profile?.speechPreferences.speechRate,
    voiceName: profile?.speechPreferences.voiceName,
  });
  const speakerRef = useRef(speaker);
  speakerRef.current = speaker;
  const [micOn, setMicOn] = useState(false);
  const micOnRef = useRef(false);
  const quietRounds = useRef(0);
  const sentences = useRef<SentenceBuffer | null>(null);
  const spokenAny = useRef(false);
  const sendRef = useRef<(text: string, voice: boolean) => Promise<void>>(async () => undefined);
  const heldRef = useRef<() => boolean>(() => false);
  const startRef = useRef<() => void>(() => undefined);
  const listeningRef = useRef(false);
  const ptt = usePushToTalk({
    language: locale,
    preferServerTranscription: profile?.speechPreferences.preferServerTranscription ?? false,
    serverTranscriptionAvailable:
      online && speechEnabled && (config?.speech.serverTranscription ?? false),
    context: 'dictation',
    onResult: (transcript) => void sendRef.current(transcript, true),
    autoStop: {
      silenceMs: 3000,
      initialSilenceMs: 8000,
      maxMs: 60_000,
      isHeld: () => heldRef.current(),
      detectEnd: (transcript) => detectDictationEnd(transcript, locale),
      onEnd: ({ reason, text }) => {
        // A tap, the release of a held button, a cancel word or the maximum end the microphone
        // mode; a pause keeps it – the answer is awaited, then the chat listens again.
        if (reason === 'tap' || reason === 'cancel-word' || reason === 'max') {
          micOnRef.current = false;
          setMicOn(false);
          return;
        }
        if (reason === 'nothing') {
          quietRounds.current++;
          if (quietRounds.current >= 3 || !micOnRef.current) {
            micOnRef.current = false;
            setMicOn(false);
            if (quietRounds.current >= 3) toast.show({ title: t('aiChat.voice.quiet') });
            return;
          }
          window.setTimeout(() => startRef.current(), 50);
          return;
        }
        quietRounds.current = 0;
        if (!text && micOnRef.current) window.setTimeout(() => startRef.current(), 50);
      },
    },
  });
  const listening = ptt.state === 'listening';
  listeningRef.current = listening;
  startRef.current = ptt.start;
  const press = useHoldOrTap({
    active: listening,
    start: () => {
      speaker.cancel();
      quietRounds.current = 0;
      micOnRef.current = true;
      setMicOn(true);
      ptt.start();
    },
    stop: ptt.stop,
  });
  heldRef.current = press.held;
  const stopVoice = () => {
    micOnRef.current = false;
    setMicOn(false);
    ptt.cancel();
    speaker.cancel();
  };
  const stopVoiceRef = useRef(stopVoice);
  stopVoiceRef.current = stopVoice;
  const micAvailable = speechEnabled && (profile?.speechPreferences.inputEnabled ?? true);
  const catalogEnabled = useFeature('ingredient_catalog');

  const chat = useAiChatConversation(
    () => {
      if (!openRef.current) {
        setUnread(true);
        toast.show({ title: t('aiChat.answered') });
      }
      // The microphone stayed on: once the voice is quiet, listen again.
      if (micOnRef.current)
        speakerRef.current.whenQuiet(() => {
          if (micOnRef.current && openRef.current && !listeningRef.current) startRef.current();
        });
    },
    (event) => {
      // The answer is read aloud sentence by sentence while it streams (turns this browser
      // started). A replayed stream starts over: nothing is spoken twice.
      if (event.type === 'init' && spokenAny.current) sentences.current = null;
      const buffer = sentences.current;
      if (!buffer) return;
      let parts: string[] = [];
      if (event.type === 'delta') parts = buffer.push(event.text);
      else if (event.type === 'tool' || event.type === 'done' || event.type === 'error')
        parts = buffer.flush();
      for (const part of parts) {
        spokenAny.current = true;
        speakerRef.current.say(part);
      }
      if (event.type === 'done' || event.type === 'error') sentences.current = null;
    },
  );
  const openRecipeId = /^\/recipes\/([0-9a-f-]{36})/i.exec(location.pathname)?.[1] ?? null;

  useEffect(() => {
    if (open) listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [chat.messages, chat.live, open, view]);

  const openPanel = useCallback(() => {
    setOpen(true);
    setUnread(false);
    void statusQuery.refetch();
  }, [statusQuery]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'j') {
        e.preventDefault();
        if (openRef.current) {
          stopVoiceRef.current();
          setOpen(false);
        } else openPanel();
      }
      if (e.key === 'Escape' && openRef.current) {
        stopVoiceRef.current();
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openPanel]);

  const status = statusQuery.data;
  if (!signedIn || !status) return null;
  if (!status.enabled && status.reason !== 'consentRequired') return null;

  const send = async (text: string, voice = false) => {
    const q = text.trim();
    if (!q || chat.busy) return;
    setInput('');
    setView('chat');
    speaker.cancel();
    sentences.current = speaker.supported && readAloud ? new SentenceBuffer() : null;
    spokenAny.current = false;
    if (!(await chat.send(q, openRecipeId, voice))) setInput(q);
  };
  sendRef.current = send;
  const closePanel = () => {
    stopVoice();
    setOpen(false);
  };
  const openCard = (card: AIChatCard) => {
    navigate(cardPath(card));
    if (window.matchMedia('(max-width: 767px)').matches) setOpen(false);
  };
  const createFromLink = async (url: string) => {
    if (linkBusy) return;
    setLinkBusy(url);
    try {
      // A YouTube link becomes a draft the way the "Rezept aus Video" page does it (Gemini watches
      // it); any other page goes through the URL import – both end on their review page.
      if (youtubeId(url)) {
        const generation = await api.ai.recipeFromVideo({ url }, crypto.randomUUID());
        toast.success(t('aiChat.createRecipeStarted'));
        navigate(`/add/ai/${generation.id}`);
      } else {
        const job = await api.imports.fromUrl(
          { url, allowAiFallback: aiConsent },
          crypto.randomUUID(),
        );
        toast.success(t('aiChat.createRecipeStarted'));
        navigate(`/add/import/${job.id}`);
      }
      if (window.matchMedia('(max-width: 767px)').matches) setOpen(false);
    } catch (e) {
      toastError(e);
    } finally {
      setLinkBusy(null);
    }
  };
  const renderLink: RenderLink = (href, label, key) => {
    const video = !!youtubeId(href);
    const link = (
      <a
        href={href}
        target="_blank"
        rel="noreferrer noopener"
        className="break-all text-primary underline"
      >
        {label}
      </a>
    );
    if (video && !status.tools.video) return <span key={key}>{link}</span>;
    return (
      <span key={key} className="inline-flex flex-wrap items-center gap-1 align-middle">
        {link}
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 px-2 text-xs"
          title={video ? t('aiChat.createRecipeVideo') : t('aiChat.createRecipeImport')}
          loading={linkBusy === href}
          disabled={linkBusy !== null && linkBusy !== href}
          data-testid="ai-chat-create-recipe"
          onClick={() => void createFromLink(href)}
        >
          <ChefHat aria-hidden /> {t('aiChat.createRecipe')}
        </Button>
      </span>
    );
  };
  // On a shopping list the scan button already sits in the corner: stack above it on phones.
  const onShoppingList = /^\/shopping\/[^/]+/.test(location.pathname);
  // …where the scanner and the microphone already sit in the corner on phones: stack above them.
  const below = onShoppingList ? (catalogEnabled ? 1 : 0) + (micAvailable ? 1 : 0) : 0;

  return (
    <>
      <button
        type="button"
        className={cn(
          'fixed right-4 z-30 flex size-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:bottom-6 md:right-6',
          below === 2
            ? 'bottom-[calc(12.5rem+env(safe-area-inset-bottom))]'
            : below === 1
              ? 'bottom-[calc(8.5rem+env(safe-area-inset-bottom))]'
              : 'bottom-[calc(4.5rem+env(safe-area-inset-bottom))]',
          open && 'hidden md:flex',
        )}
        title={t('aiChat.openHint')}
        aria-label={t('aiChat.open')}
        aria-expanded={open}
        data-testid="ai-chat-fab"
        onClick={() => (open ? closePanel() : openPanel())}
      >
        <Sparkles aria-hidden />
        {unread && !open && (
          <span
            className="absolute right-1 top-1 size-3 rounded-full bg-destructive ring-2 ring-background"
            aria-label={t('aiChat.unread')}
          />
        )}
      </button>
      {open && (
        <aside
          role="complementary"
          aria-label={t('aiChat.title')}
          data-testid="ai-chat-panel"
          className="fixed inset-0 z-40 flex flex-col border-l bg-background shadow-xl md:inset-y-0 md:left-auto md:right-0 md:w-[420px]"
        >
          <header className="flex items-center gap-1 border-b px-2 py-2">
            {view === 'list' ? (
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('common.back')}
                onClick={() => setView('chat')}
              >
                <ChevronLeft aria-hidden />
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="icon"
                title={t('aiChat.history')}
                aria-label={t('aiChat.history')}
                onClick={() => setView('list')}
              >
                <History aria-hidden />
              </Button>
            )}
            <div className="min-w-0 flex-1">
              {view === 'list' ? (
                <strong className="text-sm">{t('aiChat.history')}</strong>
              ) : (
                <>
                  <input
                    aria-label={t('aiChat.conversationTitle')}
                    className="w-full truncate bg-transparent text-sm font-semibold outline-none focus:rounded focus:ring-1 focus:ring-ring"
                    value={chat.conversation?.title || t('aiChat.newChat')}
                    readOnly={!chat.conversation}
                    maxLength={120}
                    onChange={() => undefined}
                    onBlur={(e) => {
                      const title = e.target.value.trim();
                      if (chat.conversation && title && title !== chat.conversation.title)
                        void chat.rename(title);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                    }}
                  />
                  <p className="truncate text-xs text-muted-foreground">
                    {status.model ?? ''}
                    {chat.conversation?.contextPercent != null &&
                      ` · ${t('aiChat.context', { percent: chat.conversation.contextPercent })}`}
                  </p>
                </>
              )}
            </div>
            {view === 'chat' && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  title={t('aiChat.newChat')}
                  aria-label={t('aiChat.newChat')}
                  onClick={() => {
                    stopVoice();
                    chat.reset();
                  }}
                >
                  <Plus aria-hidden />
                </Button>
                {chat.conversation?.hasSession && (
                  <Button
                    variant="ghost"
                    size="icon"
                    title={t('aiChat.compact')}
                    aria-label={t('aiChat.compact')}
                    disabled={chat.busy}
                    onClick={() => void chat.compact()}
                  >
                    <Shrink aria-hidden />
                  </Button>
                )}
                {speaker.supported && readAloud && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className={speaker.muted ? 'text-destructive' : undefined}
                    title={
                      speaker.muted ? t('aiChat.voice.unmuteHint') : t('aiChat.voice.muteHint')
                    }
                    aria-label={speaker.muted ? t('aiChat.voice.unmute') : t('aiChat.voice.mute')}
                    aria-pressed={speaker.muted}
                    data-testid="ai-chat-mute"
                    onClick={() => speaker.setMuted(!speaker.muted)}
                  >
                    {speaker.muted ? <VolumeX aria-hidden /> : <Volume2 aria-hidden />}
                  </Button>
                )}
              </>
            )}
            <Button variant="ghost" size="icon" aria-label={t('common.close')} onClick={closePanel}>
              <X aria-hidden />
            </Button>
          </header>

          {!status.enabled ? (
            <div className="p-4">
              <AiConsentNotice />
            </div>
          ) : view === 'list' ? (
            <ConversationList
              activeId={chat.conversation?.id ?? null}
              locale={i18n.language}
              onOpen={(id) => {
                void chat.load(id);
                setView('chat');
              }}
              onDelete={(id) => void chat.remove(id)}
              onNew={() => {
                chat.reset();
                setView('chat');
              }}
            />
          ) : (
            <>
              <div
                ref={listRef}
                className="flex-1 space-y-3 overflow-y-auto p-3"
                data-testid="ai-chat-messages"
              >
                {chat.messages.length === 0 && !chat.live && (
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">{t('aiChat.empty')}</p>
                    {SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        type="button"
                        className="block w-full rounded-lg border bg-card px-3 py-2 text-left text-sm hover:bg-accent"
                        onClick={() => void send(t(`aiChat.suggestions.${s}`))}
                      >
                        {t(`aiChat.suggestions.${s}`)}
                      </button>
                    ))}
                  </div>
                )}
                {chat.messages.map((m) => (
                  <MessageView
                    key={m.id}
                    role={m.role}
                    blocks={m.content}
                    status={m.status}
                    error={m.error}
                    onOpenCard={openCard}
                    renderLink={renderLink}
                  />
                ))}
                {chat.live && (
                  <MessageView
                    role="assistant"
                    blocks={chat.live.blocks}
                    status="running"
                    error={null}
                    onOpenCard={openCard}
                    renderLink={renderLink}
                    onAnswer={(askId, allow) => void chat.answerAsk(askId, allow)}
                  />
                )}
              </div>
              {chat.error && (
                <p role="alert" className="px-3 py-1 text-xs text-destructive">
                  {chat.errorCode ? t(`aiChat.errors.${chat.errorCode}`) : chat.error}
                </p>
              )}
              {(micOn || listening || ptt.state === 'processing' || speaker.speaking) && (
                <div
                  className="flex items-center gap-2 border-t bg-muted/40 px-3 py-1.5 text-xs"
                  data-testid="ai-chat-voice-bar"
                  aria-live="polite"
                >
                  <span
                    className={cn(
                      'flex min-w-0 flex-1 items-center gap-1.5 truncate',
                      listening && 'text-destructive',
                    )}
                  >
                    {listening || ptt.state === 'processing' ? (
                      <>
                        <Mic className="size-3.5 shrink-0 animate-pulse" aria-hidden />
                        {t('aiChat.voice.listening')}{' '}
                        {ptt.transcript && <em className="truncate">{ptt.transcript}</em>}
                      </>
                    ) : speaker.speaking ? (
                      <>
                        <Volume2 className="size-3.5 shrink-0 animate-pulse" aria-hidden />
                        {t('aiChat.voice.speaking')}
                      </>
                    ) : (
                      <>
                        <Mic className="size-3.5 shrink-0" aria-hidden /> {t('aiChat.voice.micOn')}
                      </>
                    )}
                  </span>
                  {speaker.supported && readAloud && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="size-8"
                      aria-label={speaker.muted ? t('aiChat.voice.unmute') : t('aiChat.voice.mute')}
                      aria-pressed={speaker.muted}
                      onClick={() => speaker.setMuted(!speaker.muted)}
                    >
                      {speaker.muted ? <VolumeX aria-hidden /> : <Volume2 aria-hidden />}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="size-8"
                    aria-label={t('aiChat.voice.stop')}
                    title={t('aiChat.voice.stop')}
                    data-testid="ai-chat-voice-stop"
                    onClick={stopVoice}
                  >
                    <X aria-hidden />
                  </Button>
                </div>
              )}
              <form
                className="flex items-end gap-2 border-t p-2 safe-bottom"
                onSubmit={(e) => {
                  e.preventDefault();
                  void send(input);
                }}
              >
                <label htmlFor="ai-chat-input" className="sr-only">
                  {t('aiChat.messageField')}
                </label>
                <Textarea
                  id="ai-chat-input"
                  rows={1}
                  value={input}
                  maxLength={8000}
                  placeholder={t('aiChat.placeholder')}
                  className="min-h-10 max-h-40 resize-none"
                  data-testid="ai-chat-input"
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void send(input);
                    }
                  }}
                />
                {micAvailable && (
                  <Button
                    type="button"
                    variant={listening ? 'default' : 'outline'}
                    size="icon"
                    aria-pressed={listening}
                    aria-label={listening ? t('aiChat.voice.micOff') : t('aiChat.voice.mic')}
                    title={ptt.adapter ? t('aiChat.voice.hint') : t('aiChat.voice.unsupported')}
                    className="select-none touch-manipulation"
                    disabled={!ptt.adapter || (chat.busy && !listening)}
                    loading={ptt.state === 'processing'}
                    data-testid="ai-chat-mic"
                    {...press.handlers}
                  >
                    <Mic aria-hidden className={listening ? 'animate-pulse' : undefined} />
                  </Button>
                )}
                {chat.busy ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    aria-label={t('aiChat.stop')}
                    title={t('aiChat.stop')}
                    data-testid="ai-chat-stop"
                    onClick={() => {
                      speaker.cancel();
                      void chat.interrupt();
                    }}
                  >
                    <Square aria-hidden />
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    size="icon"
                    aria-label={t('aiChat.send')}
                    disabled={!input.trim()}
                    data-testid="ai-chat-send"
                  >
                    <Send aria-hidden />
                  </Button>
                )}
              </form>
            </>
          )}
        </aside>
      )}
    </>
  );
}

function ConversationList({
  activeId,
  locale,
  onOpen,
  onDelete,
  onNew,
}: {
  activeId: string | null;
  locale: string;
  onOpen: (id: string) => void;
  onDelete: (id: string) => void;
  onNew: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const list = useInfiniteQuery({
    queryKey: queryKeys.aiChat.conversations(q),
    queryFn: ({ pageParam }) =>
      api.aiChat.conversations({ cursor: pageParam, limit: 20, q: q || undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows: AIChatConversationSummary[] = list.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <div className="flex-1 space-y-2 overflow-y-auto p-3">
      <input
        type="search"
        aria-label={t('aiChat.searchHistory')}
        placeholder={t('aiChat.searchHistory')}
        className="w-full rounded-md border bg-background px-3 py-2 text-sm"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <Button variant="outline" className="w-full" onClick={onNew}>
        <Plus aria-hidden /> {t('aiChat.newChat')}
      </Button>
      {rows.length === 0 && !list.isLoading && (
        <p className="text-sm text-muted-foreground">{t('aiChat.noHistory')}</p>
      )}
      {rows.map((r) => (
        <div
          key={r.id}
          className={cn(
            'flex items-start gap-1 rounded-lg border p-2',
            r.id === activeId && 'bg-accent/40',
          )}
          data-testid="ai-chat-row"
        >
          <button type="button" className="min-w-0 flex-1 text-left" onClick={() => onOpen(r.id)}>
            <strong className="block truncate text-sm">{r.title || t('aiChat.newChat')}</strong>
            <span className="block truncate text-xs text-muted-foreground">
              {r.preview ?? (r.status === 'running' ? t('aiChat.answering') : '')}
            </span>
            <span className="block text-xs text-muted-foreground">
              {formatDate(r.updatedAt, locale, true)}
            </span>
          </button>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('aiChat.delete')}
            onClick={() => {
              onDelete(r.id);
              void queryClient.invalidateQueries({ queryKey: ['ai-chat', 'conversations'] });
            }}
          >
            <Trash2 aria-hidden />
          </Button>
        </div>
      ))}
      {list.hasNextPage && (
        <Button variant="ghost" className="w-full" onClick={() => void list.fetchNextPage()}>
          {t('common.loadMore')}
        </Button>
      )}
    </div>
  );
}

function MessageView({
  role,
  blocks,
  status,
  error,
  onOpenCard,
  onAnswer,
  renderLink,
}: {
  role: 'user' | 'assistant' | 'system';
  blocks: AIChatBlock[];
  status: 'done' | 'running' | 'error' | 'interrupted';
  error: string | null;
  onOpenCard: (card: AIChatCard) => void;
  onAnswer?: (askId: string, allow: boolean) => void;
  renderLink?: RenderLink;
}) {
  const { t } = useTranslation();
  const mine = role === 'user';
  return (
    <div
      className={cn('rounded-lg p-3 text-sm', mine ? 'ml-8 bg-primary/10' : 'mr-4 border bg-card')}
      data-testid={mine ? 'ai-chat-user' : 'ai-chat-assistant'}
    >
      {blocks.map((b, i) => {
        if (b.type === 'text')
          return mine ? (
            <p key={i} className="whitespace-pre-wrap">
              {b.text}
            </p>
          ) : (
            <MarkdownLite key={i} text={b.text} renderLink={renderLink} />
          );
        if (b.type === 'tool')
          return (
            <div key={i} className="my-1">
              <p className="text-xs text-muted-foreground">
                {b.phase === 'error'
                  ? `⚠ ${t('aiChat.tools.failed', { name: t(`aiChat.tools.${b.name}.name`, { defaultValue: b.name }) })}${b.message ? `: ${b.message}` : ''}`
                  : t(`aiChat.tools.${b.name}.${b.phase}`, {
                      ...b.params,
                      defaultValue: t(`aiChat.tools.generic.${b.phase}`, { name: b.name }),
                    })}
              </p>
              {b.card && <CardView card={b.card} onOpen={onOpenCard} />}
            </div>
          );
        if (b.type === 'ask')
          return (
            <div
              key={i}
              className="my-2 rounded-md border border-warning/60 bg-warning/10 p-2"
              data-testid="ai-chat-ask"
            >
              <p className="text-sm">
                {t(`aiChat.asks.${b.tool}`, {
                  ...b.params,
                  defaultValue: t('aiChat.asks.generic', { name: b.tool }),
                })}
              </p>
              {b.answer === null && status === 'running' && onAnswer ? (
                <div className="mt-2 flex gap-2">
                  <Button size="sm" onClick={() => onAnswer(b.askId, true)}>
                    <Check aria-hidden /> {t('aiChat.allow')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onAnswer(b.askId, false)}>
                    {t('aiChat.decline')}
                  </Button>
                </div>
              ) : (
                <p className="mt-1 text-xs text-muted-foreground">
                  {b.answer === true
                    ? t('aiChat.allowed')
                    : b.answer === false
                      ? t('aiChat.declined')
                      : t('aiChat.unanswered')}
                </p>
              )}
            </div>
          );
        if (b.type === 'compacted')
          return (
            <p key={i} className="my-1 text-xs text-muted-foreground">
              <Shrink className="mr-1 inline size-3" aria-hidden />
              {t('aiChat.compacted')}
            </p>
          );
        return null;
      })}
      {status === 'running' && (
        <ProcessingIndicator compact label={t('aiChat.thinking')} className="mt-1" />
      )}
      {status === 'error' && error && <p className="mt-1 text-xs text-destructive">{error}</p>}
      {status === 'interrupted' && (
        <p className="mt-1 text-xs text-muted-foreground">{t('aiChat.interrupted')}</p>
      )}
    </div>
  );
}

function CardView({ card, onOpen }: { card: AIChatCard; onOpen: (card: AIChatCard) => void }) {
  const { t } = useTranslation();
  const Icon =
    card.kind === 'shoppingList' ? ClipboardList : card.kind === 'recipe' ? BookOpen : Sparkles;
  return (
    <div
      className="mt-1 flex items-center gap-2 rounded-md border bg-background p-2"
      data-testid="ai-chat-card"
    >
      <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      <span className="min-w-0 flex-1">
        <strong className="block truncate text-sm">{card.title}</strong>
        <span className="block text-xs text-muted-foreground">
          {t(`aiChat.cards.${card.kind}`)}
        </span>
      </span>
      <Button size="sm" variant="outline" onClick={() => onOpen(card)}>
        {t('aiChat.openCard')}
      </Button>
    </div>
  );
}
